import React, { useState } from 'react';
import { useSelector, useDispatch } from "react-redux";
import { useMsal } from "@azure/msal-react";
import { useTheme } from '@mui/material/styles';
import { ReservationType, StatusList, ReservationStatus } from 'src/utils/common';
import {
    setConfirmationNo,
    setItemReferenceStatus,
    setStatus,
    updateSupplierHotelConfirmation,
    updateSupplierServiceConfirmation,
    updateSupplierHotelStatus,
    updateSupplierServiceStatus
} from 'src/store/reservations/slices/ReservationsManagerSlice';
import { viewSummarySelectionAction } from 'src/store/reservations/actions/ReservationOverviewActions';
import ViewReservationItemRow from './ViewReservationItemRow';
import dayjs from "dayjs";
import { dateFormat } from "src/utils/dateFormat";

const ViewSummarySection = ({ setError }) => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const { reservationOverview } = useSelector((state) => state.reservationsSummaryReducer);
    const { instance, accounts } = useMsal();
    const [loadingStatusRef, setLoadingStatusRef] = useState(null);
    const [loadingConfirmationRef, setLoadingConfirmationRef] = useState(null);
    const [successStatusRef, setSuccessStatusRef] = useState(null);
    const [successConfirmationRef, setSuccessConfirmationRef] = useState(null);
    const [statusError, setStatusError] = useState(null);
    const [confirmationError, setConfirmationError] = useState(null);

    const hotelItems = reservationOverview?.Hotels || [];
    const serviceItems = reservationOverview?.Services || [];
    const allItems = [...hotelItems, ...serviceItems];

    if (allItems.length === 0) return null;

    const handleStatusChange = (value, itemReference) => {
        setSuccessStatusRef(null);
        setStatusError(null);
        dispatch(setItemReferenceStatus({ value, itemReference }));
    };

    const handleUpdateStatus = async (itemReference) => {
        const item = allItems.find(i => i.ItemReference === itemReference);
        const requestPayload = {
            PropertyId: item.PropertyId || item.ServiceId,
            ItemReference: item.ItemReference,
            Status: item.Status,
        };

        const reservationType = item.PropertyId ? ReservationType.hotel : ReservationType.service;
        const action = reservationType === ReservationType.hotel ? updateSupplierHotelStatus : updateSupplierServiceStatus;

        try {
            setLoadingStatusRef(itemReference);
            await dispatch(action(instance, accounts, reservationOverview.Id, requestPayload));
            
            // If the item is being confirmed, also update the booking status to confirmed
            if (item.Status === ReservationStatus.Confirmed) {
                dispatch(setStatus({ status: ReservationStatus.Confirmed }));
            }
            
            setStatusError(null);
            setError(null);
            setSuccessStatusRef(itemReference);
        } catch (err) {
            setError(err);
            setStatusError(err);
        } finally {
            setLoadingStatusRef(null);
        }
    };

    const handleConfirmationNoChange = (value, itemReference) => {
        setSuccessConfirmationRef(null);
        setConfirmationError(null);
        dispatch(setConfirmationNo({ itemReference, confirmationNo: value }));
    };

    const handleUpdateSummary = async (itemReference) => {
        const item = allItems.find(i => i.ItemReference === itemReference);
        const reservationType = item.PropertyId ? ReservationType.hotel : ReservationType.service;
        const action = reservationType === ReservationType.hotel ? updateSupplierHotelConfirmation : updateSupplierServiceConfirmation;
        const entityId = item.PropertyId || item.ServiceId

        const requestPayload = {
            SupplierConfirmation: item.ConfirmationNo || null,
            ItemReference: item.ItemReference,
        };

        try {
            setLoadingConfirmationRef(itemReference);
            await dispatch(action(instance, accounts, reservationOverview.Id, entityId, requestPayload));
            setConfirmationError(null);
            setError(null);
            setSuccessConfirmationRef(itemReference);
        } catch (err) {
            setError(err);
            setConfirmationError(err);
        } finally {
            setLoadingConfirmationRef(null);
        }
    };

    return (
        <>
            {allItems
                .slice()
                .sort((a, b) => {
                    const checkInA = a.CheckIn ? dayjs(a.CheckIn, dateFormat) : dayjs(0);
                    const checkInB = b.CheckIn ? dayjs(b.CheckIn, dateFormat) : dayjs(0);
                    return checkInA.diff(checkInB);
                })
                .map((item, index) => {
                    const reservationType = item.PropertyId
                        ? ReservationType.hotel
                        : ReservationType.service;
                    const config = viewSummarySelectionAction(reservationType, item);
                    if (!config) return null;

                    return (
                        <ViewReservationItemRow
                            key={item.ItemReference}
                            item={item}
                            reservationType={reservationType}
                            config={{ ...config, statusOptions: StatusList }}
                            theme={theme}
                            handleStatusChange={handleStatusChange}
                            handleUpdateStatus={handleUpdateStatus}
                            handleConfirmationNoChange={handleConfirmationNoChange}
                            handleUpdateSummary={handleUpdateSummary}
                            loadingStatusRef={loadingStatusRef}
                            loadingConfirmationRef={loadingConfirmationRef}
                            successStatusRef={successStatusRef}
                            successConfirmationRef={successConfirmationRef}
                            statusError={statusError}
                            confirmationError={confirmationError}
                        />
                    );
                })}
        </>
    );
};

export default ViewSummarySection;
